import { signal } from '@preact/signals'
import { useLocation } from 'wouter'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { ErrorBanner } from '../../components/ErrorBanner'
import { TextInput } from '../../components/TextInput'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../../routes'
import { styled } from '../../setup'
import { colors, spacing, typography } from '../../theme'

const Container = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${spacing['2xl']};
`

const Instructions = styled('p')`
  color: ${colors.textSecondary};
  font-size: ${typography.fontSizeBase};
  line-height: 1.6;
  margin: 0 0 ${spacing['2xl']} 0;
`

const Form = styled('form')`
  width: 100%;
`

// Local form state
const guardianEmail = signal('')
const isSubmitting = signal(false)
const validationError = signal<string | null>(null)

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function LinkRequestPage() {
  const { requestGuardianLink, error, clearAuthError, profile } = useAuth()
  const [, setLocation] = useLocation()

  // Redirect if already linked
  if (profile.value?.childLinkState === 'LINKED') {
    setLocation(ROUTES.PROFILE)
    return null
  }

  // Show pending state if already requested
  if (profile.value?.childLinkState === 'PENDING') {
    setLocation(ROUTES.CHILD_LINK_PENDING)
    return null
  }

  const handleSubmit = async (e: Event) => {
    e.preventDefault()

    if (!EMAIL_REGEX.test(guardianEmail.value)) {
      validationError.value = 'Please enter a valid email address'
      return
    }

    validationError.value = null
    isSubmitting.value = true
    clearAuthError()

    try {
      await requestGuardianLink(guardianEmail.value)
      guardianEmail.value = ''
      setLocation(ROUTES.CHILD_LINK_PENDING)
    } catch {
      // Error handled by store
    } finally {
      isSubmitting.value = false
    }
  }

  return (
    <Container>
      <Card title="Ask Your Parent" maxWidth="480px">
        <Instructions>
          Enter your parent or guardian's email address. We'll send them a link to connect with your
          account. They'll need to create an account if they don't have one already.
        </Instructions>

        <Form onSubmit={handleSubmit}>
          {(error.value || validationError.value) && (
            <ErrorBanner
              message={validationError.value || error.value || ''}
              onDismiss={() => {
                validationError.value = null
                clearAuthError()
              }}
            />
          )}

          <TextInput
            label="Guardian's Email"
            signal={guardianEmail}
            type="email"
            placeholder="parent@example.com"
            autoComplete="email"
            autoFocus
          />

          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting.value}
            disabled={isSubmitting.value}
            fullWidth
          >
            Send Request
          </Button>
        </Form>
      </Card>
    </Container>
  )
}
